import { SiteRateSchedule_Update } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import * as yup from 'yup';

import { commissionPercentageSchema } from '~components/PayCommission/commissionPercentageSchema';
import {
  addOnAssignmentSchema,
  testUniqueAddOnAssignments,
} from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';

type RateFieldValue = number | null | undefined | '';

const rateHasValue = (rate: RateFieldValue) =>
  rate !== undefined && rate !== null && rate !== '';

type InheritedCommissionSource = {
  inheritedVendorPayCommissionPercentage?: string | null;
  inheritedDriverPayCommissionPercentage?: string | null;
};

export const pickInheritedCommission = (source: InheritedCommissionSource) => ({
  inheritedVendorPayCommissionPercentage:
    source.inheritedVendorPayCommissionPercentage ?? null,
  inheritedDriverPayCommissionPercentage:
    source.inheritedDriverPayCommissionPercentage ?? null,
});

const siteRateSchema = yup.object().shape({
  isDeleted: yup.boolean().notRequired(),
  id: yup.string().notRequired(),
  // TRE-14560: stable client-side identifier — set on every row regardless
  // of whether the SRS exists server-side yet. Lets the resilient save flow
  // map per-rate operation results back to form rows even before a row
  // earns a server `id`. Never sent to the backend.
  clientKey: yup.string().notRequired(),
  name: yup.string().nullable().notRequired(),
  pickupSite: yup
    .object()
    .shape({
      label: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .nullable()
    .notRequired(),
  dropoffSite: yup
    .object()
    .shape({
      label: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .nullable()
    .notRequired(),
  vendorPayCommissionPercentage: commissionPercentageSchema,
  driverPayCommissionPercentage: commissionPercentageSchema,
  inheritedVendorPayCommissionPercentage: yup.string().nullable().notRequired(),
  inheritedDriverPayCommissionPercentage: yup.string().nullable().notRequired(),
  netVendorRate: yup.string().nullable().notRequired(),
  netDriverRate: yup.string().nullable().notRequired(),
  customerRate: yup
    .object()
    .shape({
      id: yup.string().notRequired(),
      rate: yup
        .number()
        .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
        .min(
          0,
          t('form_validation_errors.min', {
            field: t('dispatch.job.customer_rate'),
            min: 0,
          }),
        )
        .typeError(t('form_validation_errors.numbers_only'))
        .notRequired(),
      type: yup
        .object()
        .shape({
          label: yup.string().notRequired(),
          value: yup.string().notRequired(),
        })
        .nullable()
        .when('rate', {
          is: rateHasValue,
          then: (schema) =>
            schema.required(t('form_validation_errors.rate_type_required')),
          otherwise: (schema) => schema.notRequired(),
        }),
    })
    .nullable()
    .notRequired(),
  vendorRate: yup
    .object()
    .shape({
      id: yup.string().notRequired(),
      rate: yup
        .number()
        .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
        .min(
          0,
          t('form_validation_errors.min', {
            field: t('order.form.vendor_rate'),
            min: 0,
          }),
        )
        .typeError(t('form_validation_errors.numbers_only'))
        .notRequired(),
      type: yup
        .object()
        .shape({
          label: yup.string().notRequired(),
          value: yup.string().notRequired(),
        })
        .nullable()
        .when('rate', {
          is: rateHasValue,
          then: (schema) =>
            schema.required(t('form_validation_errors.rate_type_required')),
          otherwise: (schema) => schema.notRequired(),
        }),
    })
    .nullable()
    .notRequired(),
  // Validation is skipped entirely when `useVendorRateForDriver` is true:
  // the driver row is hidden, and submit-time `deriveDriverFromVendor`
  // overwrites driverRate with the vendor values before persisting.
  driverRate: yup
    .object()
    .shape({
      id: yup.string().notRequired(),
      rate: yup
        .number()
        .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
        .min(
          0,
          t('form_validation_errors.min', {
            field: t('dispatch.job.driver_rate'),
            min: 0,
          }),
        )
        .typeError(t('form_validation_errors.numbers_only'))
        .notRequired(),
      type: yup
        .object()
        .shape({
          label: yup.string().notRequired(),
          value: yup.string().notRequired(),
        })
        .nullable()
        .when('rate', {
          is: rateHasValue,
          then: (schema) =>
            schema.required(t('form_validation_errors.rate_type_required')),
          otherwise: (schema) => schema.notRequired(),
        }),
    })
    .nullable()
    .notRequired()
    .when('useVendorRateForDriver', {
      is: true,
      then: () => yup.mixed().nullable().notRequired(),
    }),
  customerAddOns: yup
    .array()
    .of(addOnAssignmentSchema)
    .notRequired()
    .test('unique-add-on-assignments', '', testUniqueAddOnAssignments),
  vendorAddOns: yup
    .array()
    .of(addOnAssignmentSchema)
    .notRequired()
    .test('unique-add-on-assignments', '', testUniqueAddOnAssignments),
  driverAddOns: yup
    .array()
    .of(addOnAssignmentSchema)
    .notRequired()
    .test('unique-add-on-assignments', '', testUniqueAddOnAssignments),
  material: yup
    .object()
    .shape({
      label: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .nullable()
    .notRequired(),
  equipmentType: yup
    .object()
    .shape({
      label: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .nullable()
    .notRequired(),
  useVendorRateForDriver: yup.boolean().notRequired(),
  quantity: yup
    .number()
    .transform((_, val) =>
      val === null || val === undefined || String(val).trim() === '' ? null : Number(val),
    )
    .min(
      0,
      t('form_validation_errors.min', {
        field: t('project.form.quoted_quantity'),
        min: 0,
      }),
    )
    .typeError(t('form_validation_errors.numbers_only'))
    .nullable()
    .notRequired(),
  jurisdiction: yup.string().nullable().notRequired(),
  pwOnSiteRate: yup
    .number()
    .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
    .min(
      0,
      t('form_validation_errors.min', {
        field: t('form_fields.pw_on_site_rate'),
        min: 0,
      }),
    )
    .typeError(t('form_validation_errors.numbers_only'))
    .nullable()
    .notRequired(),
  totalFringeRate: yup
    .number()
    .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
    .min(
      0,
      t('form_validation_errors.min', {
        field: t('form_fields.total_fringe_rate'),
        min: 0,
      }),
    )
    .typeError(t('form_validation_errors.numbers_only'))
    .nullable()
    .notRequired(),
});

export const FREIGHT_RATE_COVERAGE_ERROR_PATH = 'freightRateCoverage';

export const siteRatesSchema = yup.object().shape({
  // Soft-deleted rows are exempt: they are already hidden and filtered from the
  // save payload, so validating one would deadlock save on a malformed row the
  // user can no longer see. `notRequired()` (not `.strip()`) keeps the row in
  // the array so it still feeds the delete operation at save.
  siteRates: yup
    .array()
    .of(
      yup.lazy((value: SiteRateScheduleDTO | undefined) =>
        value?.isDeleted === true ? yup.mixed().notRequired() : siteRateSchema,
      ),
    )
    .required(),
});

export const hasCustomerFreightRate = (
  siteRates: (
    | Pick<SiteRateScheduleDTO, 'isDeleted' | 'customerRate'>
    | null
    | undefined
  )[] = [],
) =>
  siteRates.some(
    (rate) =>
      !rate?.isDeleted &&
      rateHasValue(rate?.customerRate?.rate) &&
      Boolean(rate?.customerRate?.type?.value),
  );

export const makeSiteRatesSchema = (isFreightRateRequired: boolean) =>
  isFreightRateRequired
    ? siteRatesSchema.test('has-customer-freight-rate', '', function (value) {
        return (
          hasCustomerFreightRate(value.siteRates) ||
          this.createError({
            path: FREIGHT_RATE_COVERAGE_ERROR_PATH,
            message: t('form_validation_errors.freight_rate_required'),
          })
        );
      })
    : siteRatesSchema;

export type SiteRateScheduleDTO = yup.InferType<typeof siteRateSchema>;
// The `siteRates` element schema is a `yup.lazy` (soft-deleted rows bypass
// validation), which yup's `InferType` cannot resolve to the element shape, so
// pin it to `siteRateSchema`'s inferred element rather than inferring the whole
// object schema.
export type SiteRatesDTO = { siteRates: SiteRateScheduleDTO[] };

// TRE-14509: pairs the rate DTO (kept for add-on processing and id lookup)
// with a *sparse* PATCH body — only the SRS fields the user actually changed
// against the originally loaded snapshot. Sending a sparse body matches PATCH
// semantics and prevents stale form state from clobbering rate fields that
// were edited concurrently by another user. An empty body signals "no SRS
// PATCH needed"; the consumer skips the request and only processes add-ons.
//
// `dirtyAddOnFormIndices` records the position each filtered add-on
// occupied in the rate's full add-on list at submit time. The
// resilient save hook reports add-on errors against the filtered indices,
// and the caller uses these maps to translate back to form indices (so the
// per-card error UI can highlight the right row).
export type SiteRateScheduleUpdateOperation = {
  rate: SiteRatesDTO['siteRates'][number];
  body: Partial<SiteRateSchedule_Update>;
  dirtyAddOnFormIndices?: {
    customer: number[];
    vendor: number[];
    driver: number[];
  };
};

export type SiteRateScheduleOperations = {
  toCreate: SiteRatesDTO['siteRates'];
  toUpdate: SiteRateScheduleUpdateOperation[];
  toDelete: SiteRatesDTO['siteRates'];
  // Selective rate apply (project scope, toggle ON): order ids the user chose to SKIP in the
  // impact dialog. Forwarded onto the project-scoped PATCH body as `skipped_order_ids` so the BE
  // pins those orders instead of cascading to them. Undefined = not gated (order scope / toggle
  // off / zero-impact) → today's behavior.
  skippedOrderIds?: string[];
  // Selective rate apply date range (TRE-15583): the impact dialog's From/To as `YYYY-MM-DD`.
  // Forwarded next to `skipped_order_ids` on the project PATCH so the BE scopes the cascade to
  // in-range orders and folds the out-of-range ones into the pin set server-side.
  from?: string;
  to?: string;
  // Selective rate apply at the ORDER level (TRE-15829): the id of the order the rate edit
  // originated from. Set only for a synced order-origin edit (which routes to the project card).
  // Forwarded as `origin_order_id` on the project PATCH so the BE excludes the origin from the pin
  // set — an order-form edit always applies to its OWN order + the project (toggle OFF pins every
  // OTHER reachable order; toggle ON leaves the origin out of the skippable list). Undefined for
  // project-origin edits (project form has no origin), preserving today's behavior there.
  originOrderId?: string;
  // Selective rate apply per-request override (TRE-15930): the order-form checkbox value,
  // forwarded as `apply_rate_changes_to_existing_orders` on the project-scoped SRS write. `true` =
  // selective apply honoring the impact dialog's unchecks; `false` = only the origin order (+ the
  // project) changes and every other reachable order is pinned. Set on BOTH toggle states for a
  // synced order-origin edit so the BE reads the intent from the request rather than the shared
  // persisted project setting. Undefined for project-origin edits, preserving today's behavior.
  applyRateChangesToExistingOrders?: boolean;
  // Durable referenced-set reconcile signal (TRE-16125): TRUE when the dedicated `rate_reference`
  // endpoint call (issued once per save by FreightRates, BEFORE these card writes — see
  // `applyProjectRateReference`) actually CHANGED the referenced set this save. Keyed off the
  // TRANSITIONS-ONLY response (newly referenced / newly dereferenced non-empty), NOT merely "the
  // dialog ran" — so a plain apply-to-all save that reconciles to a no-op leaves this false. The
  // referenced list itself is NO LONGER carried on the SRS write: it moved off the base-rate-only SRS
  // PATCH onto the order-level endpoint so it fires for the full card delta (add-on / material /
  // delete), not just base-rate edits. This flag exists only so the orchestrator can tell the user
  // WHICH HALF landed if a later card write fails ("orders now keep their own rates, but the rate
  // change didn't finish — retry"). Undefined/false when the reconcile changed nothing this save.
  rateReferenceApplied?: boolean;
};

export const createDefaultSiteRateScheduleDTO = (
  useVendorRateForDriver = false,
): SiteRateScheduleDTO => ({
  isDeleted: false,
  id: undefined,
  clientKey: crypto.randomUUID(),
  name: null,
  pickupSite: null,
  dropoffSite: null,
  customerRate: { rate: undefined, type: null },
  vendorRate: { rate: undefined, type: null },
  vendorPayCommissionPercentage: undefined,
  driverPayCommissionPercentage: undefined,
  inheritedVendorPayCommissionPercentage: null,
  inheritedDriverPayCommissionPercentage: null,
  netVendorRate: null,
  netDriverRate: null,
  driverRate: { rate: undefined, type: null },
  customerAddOns: [],
  vendorAddOns: [],
  driverAddOns: [],
  material: null,
  equipmentType: null,
  useVendorRateForDriver,
  quantity: undefined,
  jurisdiction: null,
  pwOnSiteRate: undefined,
  totalFringeRate: undefined,
});
